import React, { useState, useEffect } from 'react';
import Modal from '../shared/Modal';
import { useProductParts } from '../../hooks/useProductParts';
import { ProductPart } from '../../types/mold';

interface SearchPartModalProps {
  onClose: () => void;
  onAddPart: (partId: number, part: ProductPart) => void;
  associatedPartIds: number[];
}

const ITEMS_PER_PAGE = 10;

const SearchPartModal: React.FC<SearchPartModalProps> = ({ onClose, onAddPart, associatedPartIds }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredParts, setFilteredParts] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { productParts, loading, error, fetchProductParts } = useProductParts();

  useEffect(() => {
    fetchProductParts();
  }
  , [fetchProductParts]);

  useEffect(() => {
    const filtered = productParts.filter(
      (part) =>
        !associatedPartIds.includes(part.id) && // Filter out already associated parts
        (part.partName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          part.partNumber.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredParts(filtered);
    setCurrentPage(1); // Reset to first page on search term change
  }, [searchTerm, productParts, associatedPartIds]);

  const paginatedParts = filteredParts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const totalPages = Math.ceil(filteredParts.length / ITEMS_PER_PAGE);

  return (
    <Modal onClose={onClose} isBackgroundTransparent={true}>
      <div className="bg-white w-full max-w-md px-3 max-h-[90vh] flex flex-col">
        <h2 className="text-2xl font-bold mb-2 text-center pt-2">ค้นหาชิ้นส่วนงาน</h2>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="พิมพ์ชื่อหรือรหัสชิ้นงาน"
          data-cy="part-search-input"
          className="w-full px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
        />
        {loading ? (
          <div className="space-y-4 overflow-y-auto">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="flex justify-between items-center py-2 animate-pulse">
                <div className="flex flex-col space-y-1">
                  <div className="h-4 bg-gray-300 rounded w-24"></div>
                  <div className="h-3 bg-gray-300 rounded w-16"></div>
                </div>
                <div className="h-8 bg-gray-300 rounded-full w-16"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <p className="text-red-500 text-center">{error}</p>
        ) : (
          <div className="mt-1 overflow-y-auto flex-1 max-h-[50vh]">
            {paginatedParts.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {paginatedParts.map((part) => (
                  <li
                    key={part.id}
                    data-cy={'part-option'}
                    className="flex justify-between items-center py-2">
                    <div className="flex flex-col">
                      <span className="text-base font-medium">{part.partName}</span>
                      <span className="text-sm text-gray-500">{part.partNumber}</span>
                    </div>
                    <button
                      onClick={() => onAddPart(part.id, part)}
                      className="px-4 py-2 bg-green-600 text-white rounded-full shadow-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors text-base font-medium"
                    >
                      เพิ่ม
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-center text-gray-500">ไม่พบชิ้นส่วนงาน</p>
            )}
          </div>
        )}
        <div className="mt-4 flex justify-between items-center py-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-full shadow-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-sm font-medium disabled:opacity-50"
          >
            ก่อนหน้า
          </button>
          <span className="text-sm text-gray-700">
            หน้าที่ {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-full shadow-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-sm font-medium disabled:opacity-50"
          >
            ถัดไป
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SearchPartModal;
